import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone, Clock, MapPin, CheckCircle2, ArrowRight, Send } from "lucide-react";
import { CONTACT } from "@/constants/testIds";
import { validateUKTelephone, normalizeUKTelephone } from "@/utils/ukValidation";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    pharmacy: "",
    email: "",
    phone: "",
    address: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.pharmacy.trim()) e.pharmacy = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.phone.trim()) e.phone = "Required";
    else if (!validateUKTelephone(form.phone)) e.phone = "Please enter a valid UK telephone number.";
    if (!form.address.trim()) e.address = "Required";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("full_name", form.name);
      formData.append("pharmacy", form.pharmacy);
      formData.append("user_email_reg", form.email);
      formData.append("contact_phone", form.phone);
      formData.append("address", form.address);
      formData.append("subject", `Pharmacy Inquiry: ${form.pharmacy}`);
      formData.append("message", form.message || "");

      const response = await fetch("/assets/Contact/contact_inquiry.php", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        console.warn("PHP Endpoint response:", response.status);
        setSubmitted(true);
      }
    } catch (err) {
      console.error("Failed to post to contact_inquiry.php:", err);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-emerald" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-5"
            >
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">Contact</div>
              <h1 data-testid={CONTACT.pageTitle} className="text-[4.1rem] font-bold text-[#0A2540] tracking-tight leading-[1.05] mb-6">
                Don't miss out — let's compare your prices.
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-10">
                Send us a snapshot of what you're paying today and we'll show you, line by line, how much Cascade Plus would save your pharmacy every month.
              </p>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-0.5">Email</div>
                    <a href="mailto:info@cascadeplus.co.uk" className="text-[#0A2540] font-semibold hover:text-emerald-600 transition-colors">
                      info@cascadeplus.co.uk
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-0.5">Live assistance</div>
                    <div className="text-[#0A2540] font-semibold">Monday – Friday, 9:00 – 18:00</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-0.5">Location</div>
                    <div className="text-[#0A2540] font-semibold">United Kingdom</div>
                  </div>
                </div>
              </div>

              <div className="mt-10 rounded-2xl bg-[#0A2540] p-6 text-white relative overflow-hidden">
                <div className="absolute -top-10 -right-10 h-40 w-40 bg-emerald-500/20 blur-3xl rounded-full" />
                <div className="relative">
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-400 mb-2">Already a customer?</div>
                  <p className="text-slate-300 text-sm mb-4">Access your ordering platform directly.</p>
                  <Link
                    to="https://orderpad.cascadeplus.co.uk/"
                    className="inline-flex items-center gap-2 text-emerald-400 font-semibold hover:gap-3 transition-all"
                  >
                    Go to login page <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* RIGHT - FORM */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-7"
            >
              <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5 p-8 lg:p-10">
                {submitted ? (
                  <div data-testid={CONTACT.successMessage} className="py-12 text-center">
                    <div className="h-16 w-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-5">
                      <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A2540] mb-3">Thanks — we'll be in touch.</h3>
                    <p className="text-slate-600 max-w-md mx-auto">
                      One of our pharma veterans will reach out within one working day with your free price comparison.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-5" noValidate>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field
                        id="name"
                        label="Your name"
                        value={form.name}
                        onChange={onChange("name")}
                        error={errors.name}
                        testId={CONTACT.inputName}
                        placeholder="Jane Smith"
                      />
                      <Field
                        id="pharmacy"
                        label="Pharmacy name"
                        value={form.pharmacy}
                        onChange={onChange("pharmacy")}
                        error={errors.pharmacy}
                        testId={CONTACT.inputPharmacy}
                        placeholder="Smith's Pharmacy"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field
                        id="email"
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        error={errors.email}
                        testId={CONTACT.inputEmail}
                        placeholder="you@pharmacy.co.uk"
                      />
                      <Field
                        id="phone"
                        label="Phone"
                        value={form.phone}
                        onChange={onChange("phone")}
                        error={errors.phone}
                        testId={CONTACT.inputPhone}
                        placeholder="07000 000000"
                      />
                    </div>
                    <Field
                      id="address"
                      label="Pharmacy address"
                      value={form.address}
                      onChange={onChange("address")}
                      error={errors.address}
                      testId={CONTACT.inputAddress}
                      placeholder="123 High Street, London"
                    />
                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-[#0A2540] mb-2">
                        Anything else? <span className="text-slate-400 font-normal">(optional)</span>
                      </label>
                      <textarea
                        id="message"
                        rows={4}
                        value={form.message}
                        onChange={onChange("message")}
                        data-testid={CONTACT.inputMessage}
                        placeholder="Tell us about your current procurement setup…"
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid={CONTACT.submitButton}
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#0A2540] text-white px-6 py-4 rounded-xl font-semibold hover:bg-[#0F365C] transition-all hover:gap-3 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
                    >
                      {submitting ? "Sending..." : "Send my free comparison"}
                      <Send className="h-4 w-4" />
                    </button>
                    <p className="text-xs text-slate-500 text-center">
                      We'll only use your details to send you the comparison. No spam, ever.
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, value, onChange, error, testId, type = "text", placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-[#0A2540] mb-2">
        {label} <span className="text-rose-500">*</span>
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testId}
        placeholder={placeholder}
        className={`w-full rounded-xl border px-4 py-3 text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all ${error
            ? "border-rose-300 focus:ring-rose-200 focus:border-rose-400"
            : "border-slate-200 focus:ring-emerald-500 focus:border-emerald-500"
          }`}
      />
      {error && <p className="mt-1.5 text-xs text-rose-600 font-medium">{error}</p>}
    </div>
  );
}
